import { FINANCE, FlexBuyAvailableMonths, SpecialPaymentFormula } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { AnyAction, Dispatch } from 'redux';
import * as offerReduxSelectors from '../selectors/offerRedux';
import type { SelectFlexBuyPaymentPayload, UpdateTermAndPercentPayload } from '../types/flexBuyPaymentsTypes';
import {
    loadingUpdateFlexBuyTermPercent,
    resetPreviousFinanceInputWithoutFFB,
    resetSpecialPaymentFormula,
    selectedTerm,
    updateDownPaymentImmediate,
    updateFlexBuyPaymentGridAxisValues,
    updateFlexBuyPaymentPercent,
    updateFlexBuyTermsGrid,
    updateSpecialPaymentFormula
} from './offerRedux';
import { FLEX_BUY_TERM_LIST } from '../constants';

export const thunkUpdateTermAndPercent =
    (payload: UpdateTermAndPercentPayload) => (dispatch: Dispatch<AnyAction, any>, getState: any) => {
        const state = getState();

        const offer = offerReduxSelectors.getOffer(state);
        const useNewTermIdStandard = featureToggleSelectors.useNewTermIdStandard(state);

        const {
            selectedTermItem: term,
            selectedPercentItem: percent,
            newTermsList,
            newPercentsList,
            flexBuyDownPaymentsList,
            columnIndex,
            hasSelectedItemInColumn
        } = payload;

        const flexBuyTerms = newTermsList.map((termItem) => termItem.val).filter((v) => v != null) as number[];
        const flexBuyPercents = newPercentsList.map((percentItem) => percentItem.val).filter((v) => v != null) as number[];

        const commonPayload = {
            flexBuyDownPayments: flexBuyDownPaymentsList,
            flexBuyTerms,
            flexBuyPercents
        };

        dispatch(loadingUpdateFlexBuyTermPercent({ columnIndex }));
        dispatch(
            updateFlexBuyPaymentGridAxisValues({
                ...commonPayload
            })
        );
        dispatch(updateFlexBuyTermsGrid(commonPayload, { payment: !hasSelectedItemInColumn }));

        if (!hasSelectedItemInColumn) return;

        // @note only dispatch when term changes therefore the term is optional
        if (term?.val != null) {
            const baseTermId = `finance_term_${term.val}`;
            const newTermIdStandard = useNewTermIdStandard
                ? offerReduxSelectors.getAutoTermId(offer, term.val, {
                      offerType: FINANCE,
                      extension: {
                          DOWNPAYMENT: flexBuyDownPaymentsList[columnIndex],
                          FORDFLEXBUY: flexBuyPercents[columnIndex],
                          TERM: flexBuyTerms[columnIndex]
                      }
                  })
                : null;

            const selectedTermId = newTermIdStandard || baseTermId;
            dispatch(selectedTerm(selectedTermId, { payment: false }));
        }

        if (percent.val != null) {
            dispatch(
                updateFlexBuyPaymentPercent(percent.val, {
                    meta: {
                        paymentRequestOverride: {
                            [FINANCE]: {
                                ...commonPayload,
                                termList: FLEX_BUY_TERM_LIST
                            }
                        }
                    },
                    paymentGridOptions: {
                        isMainPayment: true
                    }
                })
            );
        }
    };

export const thunkSelectFlexBuyPayment =
    (payload: SelectFlexBuyPaymentPayload) => (dispatch: Dispatch<AnyAction, any>, getState: any) => {
        const state = getState();

        const offer = offerReduxSelectors.getOffer(state);
        const useNewTermIdStandard = featureToggleSelectors.useNewTermIdStandard(state);

        const { item, flexBuyDownPayments, flexBuyPercents, flexBuyTerms } = payload;

        const termId = useNewTermIdStandard
            ? offerReduxSelectors.getAutoTermId(offer, item.term, {
                  offerType: FINANCE,
                  extension: {
                      DOWNPAYMENT: item.downPayment,
                      FORDFLEXBUY: item.initialPaymentDiscount,
                      TERM: item.term
                  }
              }) || ''
            : `finance_term_${item.term}`;

        dispatch(updateDownPaymentImmediate(item.downPayment));
        dispatch(selectedTerm(termId, { payment: false }));
        dispatch(
            updateFlexBuyPaymentPercent(item.initialPaymentDiscount, {
                meta: {
                    paymentRequestOverride: {
                        [FINANCE]: {
                            flexBuyDownPayments,
                            flexBuyPercents,
                            flexBuyTerms,
                            termList: FLEX_BUY_TERM_LIST
                        }
                    }
                },
                paymentGridOptions: {
                    isMainPayment: true
                }
            })
        );
    };

export const thunkUpdateOfferTypeToFlexBuy =
    (payload: { shouldAddFlexBuy: boolean; defaultFordFlexBuy: number; shouldRemoveFlexBuy: boolean }) =>
    (dispatch: Dispatch<AnyAction, any>, getState: any) => {
        const state = getState();
        const { shouldAddFlexBuy, defaultFordFlexBuy, shouldRemoveFlexBuy } = payload;
        const defaultTerm = FlexBuyAvailableMonths.MONTH_66;
        const offer = offerReduxSelectors.getOffer(state);
        const useNewTermIdStandard = featureToggleSelectors.useNewTermIdStandard(state);
        const previousFinanceInputWithoutFlexBuy = offerReduxSelectors.getPreviousFinanceInputWithoutFlexBuy(state);

        if (shouldAddFlexBuy) {
            const termId = useNewTermIdStandard
                ? offerReduxSelectors.getAutoTermId(offer, defaultTerm, { offerType: FINANCE })
                : `finance_term_${defaultTerm}`;

            if (termId == null) return;

            dispatch(updateSpecialPaymentFormula(SpecialPaymentFormula.FordFlexBuy));
            dispatch(
                selectedTerm(termId, {
                    payment: false,
                    offerType: FINANCE,
                    suppressAnalytics: true
                })
            );
            dispatch(updateFlexBuyPaymentPercent(defaultFordFlexBuy, { requestPayment: false }));
        }
        if (shouldRemoveFlexBuy) {
            dispatch(resetSpecialPaymentFormula());

            if (previousFinanceInputWithoutFlexBuy) {
                dispatch(resetPreviousFinanceInputWithoutFFB());
            }
        }
    };
